import { useCallback, useMemo } from 'react';
import { CARD_COVERS_TYPE, REMOTE_ISSUE_LINKS_TYPE } from '@atlassian/jira-business-constants';
import { useIssueTypesAndFields } from '@atlassian/jira-business-entity-project';
import { useViewPreference, FIELDS_PREFERENCE } from '@atlassian/jira-business-preferences';
import { ff } from '@atlassian/jira-feature-flagging';
import {
	SUBTASKS_TYPE,
	DUE_DATE_TYPE,
	ASSIGNEE_TYPE,
	ISSUE_TYPE,
	LABELS_TYPE,
	PRIORITY_TYPE,
	SUMMARY_TYPE,
	STATUS_TYPE,
} from '@atlassian/jira-platform-field-config';
import type { BoardCardField } from '../../common/types';

// System fields' ids are the same as their types so using the type constants here
export const INITIAL_CARD_FIELDS_IDS: Set<string> = new Set([
	SUMMARY_TYPE,
	STATUS_TYPE,
	'key',
	ISSUE_TYPE,
	ASSIGNEE_TYPE,
	DUE_DATE_TYPE,
	SUBTASKS_TYPE,
	PRIORITY_TYPE,
	LABELS_TYPE,
	CARD_COVERS_TYPE,
]);

const INITIAL_CARD_FIELDS_IDS_OLD: Set<string> = new Set([
	...INITIAL_CARD_FIELDS_IDS,
	REMOTE_ISSUE_LINKS_TYPE,
]);

export const useFieldsPreference = () => {
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	const [fieldPreferences, setFieldPreferences] = useViewPreference(FIELDS_PREFERENCE) as [
		BoardCardField[] | string[],
		(fields: BoardCardField[] | string[]) => void,
	];

	const fieldIds = useMemo(() => {
		const defaultValue = ff('jwm.board-awesome-enhancements')
			? INITIAL_CARD_FIELDS_IDS
			: INITIAL_CARD_FIELDS_IDS_OLD;
		if (fieldPreferences == null || !Array.isArray(fieldPreferences)) {
			return defaultValue;
		}
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		return new Set(fieldPreferences as string[]);
	}, [fieldPreferences]);

	const setFieldIds = useCallback(
		(value: Set<string>) => setFieldPreferences(Array.from(value)),
		[setFieldPreferences],
	);

	return [fieldIds, setFieldIds] as const;
};

export const useSelectedFields = (): Record<string, boolean> => {
	const [fieldIds] = useFieldsPreference();

	return useMemo(() => {
		const selectedFields: Record<string, boolean> = {};
		fieldIds.forEach((fieldId) => {
			selectedFields[fieldId] = true;
		});

		return selectedFields;
	}, [fieldIds]);
};

/** @deprecated Remove when jwm.board-awesome-enhancements is cleaned up */
export const useCardFieldsOld = (issueTypeId: string | undefined) => {
	const selectedFields: Record<string, boolean> = useSelectedFields();
	const { typesWithFields } = useIssueTypesAndFields({
		issueOperation: 'VIEW',
	});

	return useMemo(() => {
		const matchedTypeWithFields = typesWithFields.find(
			(typeWithFields) => typeWithFields.issueType?.id === issueTypeId,
		) || { fields: [] };

		// "key" and "subtasks" are compulsory fields in the cards but not in project settings
		const fieldIdsFromSettings = [
			'key',
			SUBTASKS_TYPE,
			...matchedTypeWithFields?.fields.map((field) => field.id),
		];

		Object.keys(selectedFields).forEach((fieldId) => {
			if (fieldIdsFromSettings.includes(fieldId)) {
				selectedFields[fieldId] = true;
			} else {
				selectedFields[fieldId] = false;
			}
		});

		return selectedFields;
	}, [selectedFields, issueTypeId, typesWithFields]);
};
